import { Client } from '@stomp/stompjs';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { getAccessToken } from '@/shared/lib/auth/tokenStorage';
import {
  RealtimeContext,
  type RealtimeContextValue,
  type RealtimeEvento,
  type RealtimeStatus,
} from './realtimeContext';

type RealtimeProviderProps = {
  children: ReactNode;
  /** Conecta somente com sessao pronta. */
  enabled: boolean;
  /** Muda quando a sessao muda (ex.: troca de escola) e forca reconexao. */
  chaveSessao: string | number | null;
};

const DESTINO_FILA = '/user/queue/chat';

function apiHttpBase(): string {
  return (import.meta.env.VITE_API_URL ?? 'http://localhost:8080').replace(/\/$/, '');
}

/** Converte http(s)://host → ws(s)://host/ws?access_token=… */
function stompWsUrl(token: string): string {
  const base = apiHttpBase();
  const wsBase = base.startsWith('https')
    ? `wss${base.slice(5)}`
    : base.startsWith('http')
      ? `ws${base.slice(4)}`
      : base;
  return `${wsBase}/ws?access_token=${encodeURIComponent(token)}`;
}

/**
 * Conexao STOMP unica da sessao, compartilhada por chat e notificacoes. Sem isso
 * cada feature abriria uma conexao propria e a presenca contaria sessoes demais.
 *
 * WebSocket nativo (sem SockJS): o Spring 6 nao expoe mais jsonp e o Origin check
 * gerava WARN em loop quando o sockjs-client tentava esse transporte.
 */
export function RealtimeProvider({ children, enabled, chaveSessao }: RealtimeProviderProps) {
  const [status, setStatus] = useState<RealtimeStatus>('desconectado');
  const [abaAtiva, setAbaAtiva] = useState(true);
  const voltouDeFundo = useRef(false);
  const clientRef = useRef<Client | null>(null);
  const handlersRef = useRef(new Set<(evento: RealtimeEvento) => void>());
  const queryClient = useQueryClient();

  useEffect(() => {
    let timer: number | undefined;
    const onVis = () => {
      if (document.hidden) {
        timer = window.setTimeout(() => setAbaAtiva(false), 30_000);
        return;
      }
      if (timer != null) window.clearTimeout(timer);
      setAbaAtiva((ativa) => {
        if (!ativa) voltouDeFundo.current = true;
        return true;
      });
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      if (timer != null) window.clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    if (!enabled || chaveSessao == null || !abaAtiva) {
      clientRef.current?.deactivate();
      clientRef.current = null;
      setStatus('desconectado');
      return;
    }

    const token = getAccessToken();
    if (!token) {
      setStatus('desconectado');
      return;
    }

    const client = new Client({
      brokerURL: stompWsUrl(token),
      reconnectDelay: 5000,
      heartbeatIncoming: 10000,
      heartbeatOutgoing: 10000,
      onConnect: () => {
        setStatus('conectado');
        if (voltouDeFundo.current) {
          voltouDeFundo.current = false;
          void queryClient.invalidateQueries();
        }
        client.subscribe(DESTINO_FILA, (frame) => {
          try {
            const raw = JSON.parse(frame.body) as {
              tipo?: string;
              payload?: Record<string, unknown>;
            };
            if (!raw.tipo) return;
            const evento: RealtimeEvento = { tipo: raw.tipo, payload: raw.payload ?? {} };
            handlersRef.current.forEach((handler) => handler(evento));
          } catch {
            /* payload invalido ignorado */
          }
        });
      },
      onDisconnect: () => setStatus('desconectado'),
      onStompError: () => setStatus('desconectado'),
      onWebSocketClose: () => setStatus('desconectado'),
    });

    setStatus('conectando');
    client.activate();
    clientRef.current = client;

    return () => {
      void client.deactivate();
      if (clientRef.current === client) clientRef.current = null;
      setStatus('desconectado');
    };
  }, [enabled, chaveSessao, abaAtiva, queryClient]);

  const subscribe = useCallback((handler: (evento: RealtimeEvento) => void) => {
    handlersRef.current.add(handler);
    return () => {
      handlersRef.current.delete(handler);
    };
  }, []);

  const publish = useCallback((destino: string, body: unknown) => {
    const client = clientRef.current;
    if (!client?.connected) return;
    client.publish({ destination: destino, body: JSON.stringify(body) });
  }, []);

  const value = useMemo<RealtimeContextValue>(
    () => ({
      status,
      conectado: status === 'conectado',
      subscribe,
      publish,
    }),
    [status, subscribe, publish],
  );

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}
